import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import {
  formatAnalyticsReferenceDateLong,
  getAnalyticsReferenceDateParams,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type ExportFormat = 'markdown' | 'csv' | 'html';
type ExportRange = '7d' | '30d' | '90d' | 'ytd' | 'all' | 'custom';

interface HabitExportDialogProps {
  open: boolean;
  habitIds: number[];
  title?: string;
  onClose: () => void;
}

const todayYmd = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;
};

const thirtyDaysAgoYmd = (): string => {
  const day = new Date();
  day.setDate(day.getDate() - 29);
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(
    day.getDate(),
  ).padStart(2, '0')}`;
};

const filenameFromDisposition = (header?: string): string | null => {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch ? plainMatch[1] : null;
};

const fallbackFilename = (format: ExportFormat, count: number): string => {
  const suffix = format === 'markdown' ? 'md' : format;
  if (format === 'csv' && count > 1) return `habit-reports-${todayYmd()}.zip`;
  return `habit-report-${todayYmd()}.${suffix}`;
};

const HabitExportDialog: React.FC<HabitExportDialogProps> = ({
  open,
  habitIds,
  title,
  onClose,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const { asOfDate, enabled: analyticsReferenceDateEnabled } = useAnalyticsReferenceDate();
  const [format, setFormat] = useState<ExportFormat>('markdown');
  const [range, setRange] = useState<ExportRange>('30d');
  const [startDate, setStartDate] = useState(thirtyDaysAgoYmd);
  const [endDate, setEndDate] = useState(todayYmd);
  const [includeLogs, setIncludeLogs] = useState(true);
  const [includeStats, setIncludeStats] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeExternalIds, setIncludeExternalIds] = useState(false);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const [includeSourceMetadata, setIncludeSourceMetadata] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const habitCount = habitIds.length;
  const dialogTitle = useMemo(() => {
    if (title) return title;
    return habitCount === 1 ? 'Export Habit' : `Export ${habitCount} Habits`;
  }, [title, habitCount]);

  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);

  const handleDownload = async () => {
    if (habitIds.length === 0) {
      setError('Select at least one habit to export');
      return;
    }
    if (range === 'custom' && (!startDate || !endDate || startDate > endDate)) {
      setError('Choose a valid custom date range');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(
        `${API_BASE_URL}/api/habits/export`,
        {
          habit_ids: habitIds,
          format,
          range,
          start_date: range === 'custom' ? startDate : undefined,
          end_date: range === 'custom' ? endDate : undefined,
          include_logs: includeLogs,
          include_stats: includeStats,
          include_notes: includeNotes,
          include_external_ids: includeExternalIds,
          include_timestamps: includeTimestamps,
          include_source_metadata: includeSourceMetadata,
          tz_offset: new Date().getTimezoneOffset(),
          ...getAnalyticsReferenceDateParams(analyticsReferenceDateEnabled, asOfDate),
        },
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        filenameFromDisposition(response.headers['content-disposition']) ||
        fallbackFilename(format, habitIds.length);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to export habits'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {habitCount} {habitCount === 1 ? 'habit' : 'habits'} selected
        </Typography>

        {analyticsReferenceDateEnabled && asOfDate && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This export will use analytics as of {formatAnalyticsReferenceDateLong(asOfDate)}.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Format</InputLabel>
              <Select
                value={format}
                label="Format"
                onChange={(event) => setFormat(event.target.value as ExportFormat)}
              >
                <MenuItem value="markdown">Markdown</MenuItem>
                <MenuItem value="html">HTML</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Date range</InputLabel>
              <Select
                value={range}
                label="Date range"
                onChange={(event) => setRange(event.target.value as ExportRange)}
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
                <MenuItem value="ytd">Year to date</MenuItem>
                <MenuItem value="all">All time</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {range === 'custom' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 2, display: 'grid', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeLogs}
                onChange={(event) => setIncludeLogs(event.target.checked)}
              />
            }
            label="Include log history"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeStats}
                onChange={(event) => setIncludeStats(event.target.checked)}
              />
            }
            label="Include stats"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeNotes}
                onChange={(event) => setIncludeNotes(event.target.checked)}
              />
            }
            label="Include notes"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeExternalIds}
                onChange={(event) => setIncludeExternalIds(event.target.checked)}
              />
            }
            label="Include external IDs"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeTimestamps}
                onChange={(event) => setIncludeTimestamps(event.target.checked)}
              />
            }
            label="Include timestamps"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeSourceMetadata}
                onChange={(event) => setIncludeSourceMetadata(event.target.checked)}
              />
            }
            label="Include source metadata"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} /> : <FileDownloadIcon />}
          onClick={handleDownload}
          disabled={loading || habitIds.length === 0}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HabitExportDialog;
